import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import { Check, UserPlus } from "lucide-react";
import { ApiError } from "@atlas/shared";
import { fetchJoinInfo, storeReferral } from "../api/referrals";
import { useAuth } from "../hooks/useAuth";
import { usePageMeta } from "../hooks/usePageMeta";

// Invite a friend landing (spec 21 / ATL-96). Public, noindex. Stores the
// referrer's username in localStorage on load so the signup form can post
// `referralCode` even if the visitor wanders off and comes back later.

const CARD_STYLE: React.CSSProperties = {
  background: "var(--surface-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: "var(--radius-xl)",
  padding: "var(--space-8)",
  boxShadow: "var(--shadow-md), var(--ring-top)",
};

const PERKS = [
  "Every walk you've already done, matched against peaks, trails and landmarks",
  "Collections and regions that fill in as you go",
  "You both get 200 Waypoints once your first walk is logged",
];

function GoldLinkButton({ to, search, children }: {
  to: string;
  search?: Record<string, string>;
  children: string;
}) {
  return (
    <Link
      to={to}
      search={search}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "var(--control-lg)",
        background: "var(--accent)",
        color: "var(--text-on-gold)",
        borderRadius: "var(--radius-md)",
        fontFamily: "var(--font-sans)",
        fontSize: "var(--text-base)",
        fontWeight: 600,
        textDecoration: "none",
      }}
    >
      {children}
    </Link>
  );
}

function ReferrerAvatar({ name, avatarUrl }: { name: string; avatarUrl: string | null }) {
  const initials = name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: "50%",
        overflow: "hidden",
        flexShrink: 0,
        border: "2px solid var(--accent)",
        background: "var(--surface-raised)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: 15,
        color: "var(--text-secondary)",
      }}
    >
      {avatarUrl ? (
        <img src={avatarUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        initials || "?"
      )}
    </div>
  );
}

function isMobileBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad/.test(navigator.userAgent);
}

export default function JoinPage() {
  const { username } = useParams({ strict: false }) as { username: string };
  const search = useSearch({ strict: false }) as { invite?: string };
  const { user, isLoading: authLoading } = useAuth();

  React.useEffect(() => {
    if (username) storeReferral(username);
  }, [username]);

  usePageMeta({
    title: "You're invited to Atlas",
    description: "Join Atlas from a friend's invite and keep your walks on the record.",
    canonicalPath: `/join/${username}`,
    noindex: true,
  });

  const joinQuery = useQuery({
    queryKey: ["join", username],
    queryFn: () => fetchJoinInfo(username),
    retry: false,
  });

  const signupSearch: Record<string, string> = { ref: username };
  if (search.invite) signupSearch["invite"] = search.invite;

  if (joinQuery.isLoading || authLoading) {
    return (
      <div style={CARD_STYLE}>
        <p style={{ fontSize: 14, color: "var(--text-muted)" }}>Checking your invite…</p>
      </div>
    );
  }

  const notFound = joinQuery.isError && joinQuery.error instanceof ApiError && joinQuery.error.status === 404;

  if (joinQuery.isError) {
    return (
      <div style={CARD_STYLE}>
        <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", marginBottom: 10 }}>
          {notFound ? "That invite link doesn't point anywhere" : "Couldn't load this invite"}
        </h1>
        <p style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 20 }}>
          {notFound
            ? "The username in the link may have been typed wrongly. You can still create a free account."
            : "Something went wrong on our side. You can still create a free account."}
        </p>
        <GoldLinkButton to="/signup">Create a free account</GoldLinkButton>
      </div>
    );
  }

  const referrer = joinQuery.data?.referrer ?? null;
  const reward = joinQuery.data?.reward.invitee ?? 200;
  const displayName = referrer ? (referrer.name?.trim() || referrer.username) : null;

  return (
    <div style={CARD_STYLE}>
      <div style={{ marginBottom: "var(--space-6)" }}>
        <div className="eyebrow" style={{ marginBottom: "var(--space-2)", display: "flex", alignItems: "center", gap: 6 }}>
          <UserPlus size={12} />
          Invited to Atlas
        </div>
        <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)" }}>
          {displayName
            ? `${displayName} thinks you should be keeping track.`
            : "A friend thinks you should be keeping track."}
        </h1>
        {referrer && displayName && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
            <ReferrerAvatar name={displayName} avatarUrl={referrer.avatarUrl} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{displayName}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                @{referrer.username} · Level {referrer.level}
              </div>
            </div>
          </div>
        )}
        <p style={{ fontSize: 14, color: "var(--text-secondary)", marginTop: 12 }}>
          Atlas turns the walks you've already done into collections, regions and
          achievements. Connect a source, and the record fills itself in.
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: "var(--space-6)" }}>
        {PERKS.map((perk) => (
          <div key={perk} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.45 }}>
            <Check size={13} style={{ color: "var(--text-accent)", flexShrink: 0, marginTop: 3 }} />
            {perk.replace("200 Waypoints", `${reward} Waypoints`)}
          </div>
        ))}
      </div>

      {user ? (
        <>
          <p style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 14 }}>
            You're already signed in, so this invite can't be applied to your account.
          </p>
          <GoldLinkButton to="/dashboard">Go to your dashboard</GoldLinkButton>
        </>
      ) : (
        <>
          <GoldLinkButton to="/signup" search={signupSearch}>
            Create your free account
          </GoldLinkButton>
          <p style={{ marginTop: "var(--space-4)", fontSize: 12, color: "var(--text-muted)", textAlign: "center" }}>
            Already have an account?{" "}
            <Link to="/login" style={{ color: "var(--text-accent)", fontWeight: 600 }}>
              Sign in
            </Link>
            . Invites only count for new accounts.
          </p>
        </>
      )}

      {isMobileBrowser() && (
        <p style={{ marginTop: "var(--space-3)", fontSize: 12, color: "var(--text-muted)", textAlign: "center" }}>
          <a href={`atlas://join/${encodeURIComponent(username)}`} style={{ color: "var(--text-accent)", fontWeight: 600 }}>
            Open in the Atlas app
          </a>
        </p>
      )}
    </div>
  );
}
